import { createContext, useEffect, useContext, useState, useCallback, useMemo, useSyncExternalStore } from "react";
import { menaZeme, odebirejZemi, zemeTenanta } from "@/lib/tenantMena";
import { sJednotkou } from "@/lib/castka";
import { dosad, jsouParametry, priponaPluralu } from "@/lib/i18n/pravidla";
import { localeJazyka } from "@/lib/i18n/format";

const LanguageContext = createContext(null);

/* JAZYKY APLIKACE — VŠECHNY se nabízejí. Rozhodnutí majitele 17. 8. 2026:
 * „tyto jazyky musí být — ukrajinština, angličtina, vše!!"
 *
 * Do 17. 8. 2026 nabízela SettingsPage jen cs+sk, protože měla seznam napsaný
 * natvrdo místo tohohle pole. Ukrajinština a vietnamština byly přeložené na
 * 100 %, ale nevedlo k nim žádné tlačítko.
 *
 * Pokrytí klíč po klíči proti češtině (973 klíčů), stav 17. 8. 2026:
 *   cs 973 (základ) · sk 973 (100 %) · uk 973 (100 %) · vi 973 (100 %)
 *   en 278 (28,6 %) — dodělává se; hlídá contexts/__tests__/jazykyVNabidce.test.js
 *
 * ⚠️ Slovník není celý příběh: ~1 764 míst ve 136 souborech má text napsaný
 * ternárem `lang === "sk" ? … : …`, tedy zná JEN češtinu a slovenštinu.
 * Ukrajinec i Vietnamec tam spadnou do české větve. Převod těchhle míst do
 * t() je samostatná práce — dokud neproběhne, není 100 % ve slovníku totéž
 * co 100 % na obrazovce.
 */
/* `kod` = textová zkratka jazyka do přepínače (CS, SK, EN, UK, VI). Vlajky
 * tu byly do 30. 9. 2026 — Windows je nekreslí (místo 🇨🇿 dvě písmena
 * v rámečku) a jazyk není země (angličtina ≠ Británie, čeština ≠ tenant CZ).
 * Kód jazyka je ISO 639-1 velkými písmeny, NE kód země: ukrajinština je UK. */
const LANGUAGES = [
  { code: "cs", name: "Čeština", kod: "CS" },
  { code: "sk", name: "Slovenčina", kod: "SK" },
  { code: "en", name: "English", kod: "EN" },
  { code: "uk", name: "Українська", kod: "UK" },
  { code: "vi", name: "Tiếng Việt", kod: "VI" },
];

/* JAZYK URČUJE JEN FORMÁT ČÍSLA, NE MĚNU.
 *
 * Do 2026-08 tu byla tabulka CURRENCY_BY_LANG, kde `sk` neslo `rate: 25`.
 * Český poradce, který si kvůli slovenskému klientovi přepnul appku do
 * slovenštiny, viděl všechny částky vydělené 25 a označené €, zatímco v DB
 * zůstaly koruny. Měna teď přichází ze ZEMĚ TENANTA (viz lib/tenantMena.js),
 * jazyk už rozhoduje jen o oddělovačích tisíců a desetinné čárce.
 *
 * ⚠️ SAMA TABULKA UŽ TADY NEBYDLÍ. Přestěhovala se do `lib/i18n/format.js`,
 * protože z ní vedle `currency.locale` čte i formátování dat a desetinných
 * čísel (`kratkeDatum`, `desetinne`). Dvě kopie by se rozešly a číslo v grafu
 * by mělo jiný oddělovač než popisek pod ním. Tam je i vysvětlení, proč
 * uk/vi/en míří schválně na cs-CZ.
 */

/* KURZ NEEXISTUJE — a proto je natvrdo 1.
 *
 * V repu není zdroj kurzu (žádné ČNB API, žádné ruční pole na úrovni appky),
 * takže se žádný přepočet nedělá: co je uložené, to se zobrazí. `rate` tu
 * zůstává jen proto, že z něj čte ~30 míst v kódu (`x / currency.rate`), kde
 * je to teď korektní no-op. NEVRACEJ SEM KURZ. Kdyby bylo přepočítávání někdy
 * opravdu potřeba, musí být kurz VIDĚT a nastavitelný uživatelem i s datem —
 * vzor je `lib/analyzaV2/meny.js` (fx_rate + fx_updated_at + expirace 30 dní).
 */
const BEZ_PREPOCTU = 1;

import { nastavJazykUI } from "@/lib/prekladyUI";

let translations = {};

export function registerTranslations(lang, data) {
  translations[lang] = data;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const ulozeny = localStorage.getItem("bp_lang") || "cs";
    /* Sdílený slovník UI (lib/prekladyUI.js) drží jazyk v modulové proměnné,
     * protože `TL()` se volá i tam, kde `lang` z kontextu není v dosahu.
     * Nastavuje se SYNCHRONNĚ v inicializátoru (ne v useEffect), aby ho měl
     * správně už úplně první render — jinak by obrazovka blikla česky. */
    nastavJazykUI(ulozeny);
    return ulozeny;
  });
  /* Každá změna jazyka se musí propsat i do sdíleného slovníku. Render, který
   * po setLang() následuje, už čte novou hodnotu. */
  nastavJazykUI(lang);

  /* PŘEKLADAČ `t` — tři věci naráz, ať se nemusí volat tři různé funkce.
   * ==========================================================================
   *
   *   t("klic")                          → text ve zvoleném jazyce
   *   t("klic", "český fallback")        → …a když klíč chybí, tenhle text
   *   t("klic", { n: 3 })                → dosadí {n} do hodnoty ze slovníku
   *   t("klic", "Zbývá {n}", { n: 3 })   → obojí najednou
   *   t.plural("zaklad", 5)              → správný tvar podle počtu
   *
   * PROČ PARAMETRY: do 8/2026 uměl `t` jen `(key, fallback)`, takže věta
   * s číslem se skládala v JSX na kusy — `t("uver")+" "+x+" Kč na "+y+" let"`.
   * Tím je pořadí částí zabetonované česky. Ukrajinština a vietnamština mají
   * slovosled jinde, takže poskládaná věta v nich nedává smysl. Se zástupnými
   * symboly si pořadí určí každý jazyk sám ve svém slovníku.
   *
   * ⚠️ ZPĚTNÁ KOMPATIBILITA je tu to hlavní — `t(key, fallback)` se v repu
   * volá na tisících míst a nesmí se pohnout ani o písmeno:
   *   • rozlišení fallback × parametry řeší `jsouParametry` (jen PROSTÝ objekt
   *     je parametry; řetězec, číslo, pole i React element jsou fallback),
   *   • dosazování běží JEN nad řetězcem a JEN když parametry přijdou —
   *     slovník má i pole hodnot (`tools.checklist.cols`) a ta musí projít
   *     nedotčená, ne přes `String()`,
   *   • pořadí hledání zůstává: zvolený jazyk → čeština → fallback → klíč.
   */
  const t = useMemo(() => {
    /* Bezpečné čtení `a.b.c`. Vrací i `null`/`""` tak, jak ve slovníku jsou —
     * rozhodnutí „chybí" dělá až volající, stejně jako před touhle změnou. */
    const projdi = (koren, key) => {
      let uzel = koren;
      for (const kus of String(key).split(".")) {
        if (uzel == null) break;
        uzel = uzel[kus];
      }
      return uzel;
    };

    const prelozit = (key, fallback, params) => {
      const vlastni = projdi(translations[lang], key);
      const hodnota = vlastni != null ? vlastni : (projdi(translations.cs, key) ?? fallback ?? key);
      return params && typeof hodnota === "string" ? dosad(hodnota, params) : hodnota;
    };

    const fn = (key, druhy, treti) => {
      const paramyDruhy = jsouParametry(druhy);
      return prelozit(
        key,
        paramyDruhy ? undefined : druhy,
        paramyDruhy ? druhy : (jsouParametry(treti) ? treti : undefined)
      );
    };

    /* MNOŽNÉ ČÍSLO. Slovník musí mít trojici `<zaklad>One/Few/Many`; kategorii
     * vybírá `Intl.PluralRules` podle jazyka, ne české pravidlo 1 / 2–4 / 5+.
     * Ověřeno nad všemi trojicemi ve slovníku: cs, sk, en i vi dají pro 1–101
     * doslova týž text jako dosavadní ruční pravidlo, ukrajinština opraví 33
     * čísel (21–24, 31–34, … 91–94, 101) a navíc všech pět opraví nulu
     * („0 roky" → „0 let"). Podrobnosti v `lib/i18n/pravidla.js`.
     *
     * `{n}` je k dispozici automaticky, ať trojice může znít i „{n} kroků".
     * Když trojice ve slovníku chybí, vrátí se holý klíč — to je schválně
     * hlučné selhání, které hlídač `__tests__/jazykBezCestiny.test.js` uvidí.
     * NEVYMÝŠLEJ chybějící trojici v kódu; patří do všech pěti slovníků. */
    fn.plural = (zaklad, n, params) => fn(`${zaklad}${priponaPluralu(lang, n)}`, { n, ...(jsouParametry(params) ? params : null) });
    fn.lang = lang;
    return fn;
  }, [lang]);

  const setLanguage = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem("bp_lang", newLang);
  }, []);

  /* Jazyk veřejné stránky poradce BEZ uložení (WV-1, 19. 10. 2026): klient SK
   * poradce dostane slovenštinu, ale jeho vlastní volba jazyka v prohlížeči se
   * nepřepíše. Volá jen `hooks/useZemePoradce.js`. */
  const nastavJazykStranky = useCallback((newLang) => setLang(newLang), []);

  /* Měna ze ZEMĚ TENANTA (AuthProvider ji zapíše z /auth/me), formát z jazyka.
   * LanguageProvider je v App.js NAD AuthProviderem, proto externí store
   * místo hooku — detaily v lib/tenantMena.js. */
  const zeme = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta);

  const currency = useMemo(() => {
    const m = menaZeme(zeme);
    return {
      code: m.code,
      symbol: m.symbol,
      locale: localeJazyka(lang),
      rate: BEZ_PREPOCTU, // vždy 1 — nepřepočítáváme, viz komentář nahoře
    };
  }, [zeme, lang]);

  /* @deprecated Zbylo kvůli ~27 voláním v kódu. Přepočet se NEDĚLÁ, takže obě
   * funkce jsou identita. Nová místa je nevolej — ber hodnotu, jak přišla. */
  const toDisplay = useCallback((value) => value, []);
  const fromDisplay = useCallback((value) => value, []);

  const formatMoney = useCallback((value, opts = {}) => {
    if (value == null || isNaN(value)) return "";
    const { decimals = 0, withSymbol = true } = opts;
    // Jazyk mění JEN oddělovače (1 000 000 vs 1 000 000), nikdy hodnotu.
    const formatted = new Intl.NumberFormat(currency.locale, {
      maximumFractionDigits: decimals,
      minimumFractionDigits: 0,
    }).format(value);
    if (!withSymbol) return formatted;
    /* NEZLOMITELNÁ mezera před symbolem — s obyčejnou ji prohlížeč v úzké
       kartě použije jako místo zlomu a „Kč“ spadne pod číslo (nález 8/2026,
       lib/castka.js). Tisíce už nezlomitelné jsou, ty dělá Intl sám. */
    return sJednotkou(formatted, currency.symbol);
  }, [currency]);

  return (
    <LanguageContext.Provider value={{
      lang,
      setLanguage,
      nastavJazykStranky,
      t,
      LANGUAGES,
      currency,
      toDisplay,
      fromDisplay,
      formatMoney,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

/* Jako useLanguage, ale bez provideru vrátí null (sdílené komponenty základu). */
export function useLanguageVolitelne() {
  return useContext(LanguageContext);
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be within LanguageProvider");
  return ctx;
}

export { LANGUAGES };
